/**
 * The to-do deck: the same list the Next card ranks (core next.ts `rankNext`), one task per full card, on web and phone.
 * A pure core, so both apps behave the same and every rule is tested at a pinned clock (no wall-clock tests).
 *
 * | Rule | Why |
 * |---|---|
 * | D1 The deck IS the Next order: it is `rankNext`, never a second sort | Two orders would disagree about what comes next. The first card is always the Next card |
 * | D2 A safety gate (the check-in, lessons) or a stop-work is `locked`: it comes first, alone, and cannot be put off | Nothing else can be done before it (next.ts); a swipe must not hide it |
 * | D3 Right = do it (start the flow). Left = Later: the card goes to the back for a short while and then returns to its own place in the order. Never gone | A required check is never dismissed for good; Later is a pause with an end |
 * | D4 Later is shorter for a safety-critical card (5 min, never past the time it turns late) and not offered once a critical card is late | Food Code 3-501.16(A): holding temperatures cannot wait; a late critical check insists (next.ts N1) |
 * | D5 When a card comes due while the deck is open, it moves to the top with a "Now due" badge, except that it never goes above a safety-critical card when it is not critical itself | The person is told in the moment; a lesser task never buries a food-safety one |
 * | D6 The time indicator states the clock: "Due 2:00 PM, in 25 min", amber from 30 minutes before, red once late. Late is `isLate` (past the late time), as everywhere | One definition of late (schedule.ts) on every screen |
 * | D7 Every gesture has a button (Do it, Later), and the same keys: Right Arrow = do it, Left Arrow = Later | Accessibility: a swipe is never the only way |
 */
import type { IsoDateTime } from "./model.ts";
import { rankNext, type NextAction, type NextGlyph, type NextInput, type NextItem, type NextKind } from "./next.ts";

/** The two views of the to-do screen. The list is today's default. */
export type TodoView = "list" | "cards";
export const TODO_VIEWS: readonly TodoView[] = ["list", "cards"];
export const DEFAULT_TODO_VIEW: TodoView = "list";
export const todoViewOf = (v: unknown): TodoView => (v === "cards" ? "cards" : DEFAULT_TODO_VIEW);

/** The words on the toggle (web and phone say the same). */
export const TODO_VIEW_WORDS = { label: "How to see your tasks", list: "List", cards: "Cards" } as const;

export interface DeckCard {
  /** Stable for the task (the check's key, the problem's id): the same card keeps the same id as the clock moves. */
  id: string;
  kind: NextKind;
  tone: NextItem["tone"];
  glyph: NextGlyph;
  title: string;
  why: string;
  where?: string;
  critical: boolean;
  /** D2: cannot be put off. */
  locked: boolean;
  dueAt?: IsoDateTime;
  lateAt?: IsoDateTime;
  firstDay?: true;
  primary: { label: string; action: NextAction };
  deferredLine?: string;
  /** Delegation (delegate.ts, next.ts N6): "Dana asked you." / "Susan's."; and, when it is someone else's, the step-in the button records. */
  assign?: NextItem["assign"];
  stepIn?: NextItem["stepIn"];
}

export interface Deck {
  /** The open tasks in the Next order (empty when nothing is open). */
  cards: DeckCard[];
  /** When nothing is open: the "everything is done" card (it still has a button, N4). */
  done?: NextItem;
}

const LOCKED_KINDS: ReadonlySet<NextKind> = new Set(["gate", "stop_work"]);

/** D1: the deck, from the same input the Next card gets. */
export function buildDeck(i: NextInput): Deck {
  const ranked = rankNext(i);
  if (ranked[0]!.bucket === "done") return { cards: [], done: ranked[0]!.item };
  return {
    cards: ranked.map(({ item: n }) => ({
      id: n.id, kind: n.kind, tone: n.tone, glyph: n.glyph, title: n.title, why: n.why, critical: n.critical, locked: LOCKED_KINDS.has(n.kind), primary: n.primary,
      ...(n.where ? { where: n.where } : {}), ...(n.dueAt ? { dueAt: n.dueAt } : {}), ...(n.lateAt ? { lateAt: n.lateAt } : {}), ...(n.firstDay ? { firstDay: n.firstDay } : {}),
      ...(n.deferredLine ? { deferredLine: n.deferredLine } : {}),
      ...(n.assign ? { assign: n.assign } : {}), ...(n.stepIn ? { stepIn: n.stepIn } : {}),
    })),
  };
}

/* ─────────────── the time indicator (D6) ─────────────── */

/** Amber from this many minutes before the due time. A judgment default (ASSUMED), not a legal number. */
export const DUE_SOON_MIN = 30;
/** The ring fills over this many minutes before the due time (the same hour the Next order treats as "due now"). */
export const DUE_WINDOW_MIN = 60;

export type DueState = "none" | "ok" | "soon" | "late";
export interface DueIndicator {
  state: DueState;
  /** "Due 2:00 PM · in 25 min", "Due 2:00 PM · now", "Was due 2:00 PM · 12 min ago". Empty when the card has no time. */
  label: string;
  /** 0 to 1: how far toward the due time. Full once due. */
  progress: number;
  /** Minutes until due (negative once past), rounded up. Null with no time. */
  minutes: number | null;
}

const span = (m: number) => (m < 60 ? `${m} min` : m % 60 === 0 ? `${m / 60} h` : `${Math.floor(m / 60)} h ${m % 60} min`);

/** D6. `timeLabel` is the app's clock wording ("2:00 PM"), so the zone stays the app's. */
export function dueIndicator(c: Pick<DeckCard, "dueAt" | "lateAt" | "firstDay">, nowIso: IsoDateTime, timeLabel: (iso: IsoDateTime) => string): DueIndicator {
  if (!c.dueAt) return { state: "none", label: "", progress: 0, minutes: null };
  const left = (Date.parse(c.dueAt) - Date.parse(nowIso)) / 60_000;
  const minutes = Math.ceil(left);
  const late = !c.firstDay && !!c.lateAt && nowIso > c.lateAt;
  const t = timeLabel(c.dueAt);
  const rel = minutes > 0 ? `in ${span(minutes)}` : minutes === 0 ? "now" : `${span(-minutes)} ago`;
  const progress = Math.min(1, Math.max(0, 1 - left / DUE_WINDOW_MIN));
  if (late) return { state: "late", label: `Was due ${t} · ${rel}`, progress: 1, minutes };
  return { state: left <= DUE_SOON_MIN ? "soon" : "ok", label: `Due ${t} · ${rel}`, progress, minutes };
}

/* ─────────────── Later (D3, D4) ─────────────── */

/** A pause for a card that can wait. */
export const LATER_MIN = 15;
/** A shorter pause for a safety-critical card. */
export const LATER_CRITICAL_MIN = 5;

const plusMin = (iso: IsoDateTime, m: number) => new Date(Date.parse(iso) + m * 60_000).toISOString();

export interface LaterAnswer {
  ok: boolean;
  /** When the card comes back to its own place. */
  until?: IsoDateTime;
  /** Why not, in plain words (shown on the disabled button and read by a screen reader). */
  reason?: string;
}

/** D3, D4: can this card be put off now, and until when. */
export function laterOf(c: Pick<DeckCard, "locked" | "critical" | "lateAt" | "firstDay">, nowIso: IsoDateTime): LaterAnswer {
  if (c.locked) return { ok: false, reason: "This one comes first. It can't wait." };
  const late = !c.firstDay && !!c.lateAt && nowIso > c.lateAt;
  if (c.critical && late) return { ok: false, reason: "A food safety check that is late can't wait." };
  let until = plusMin(nowIso, c.critical ? LATER_CRITICAL_MIN : LATER_MIN);
  // It comes back before it turns late (a card still on time is never put past its deadline).
  if (c.lateAt && !c.firstDay && !late && c.lateAt > nowIso && c.lateAt < until) until = c.lateAt;
  return { ok: true, until };
}

/* ─────────────── arranging the deck (D2, D3, D5) ─────────────── */

/** How long a "Now due" badge, and the card's place at the top, last. */
export const NOW_DUE_MIN = 10;

export interface DeckState {
  /** Card id to the time its Later ends. */
  later: Readonly<Record<string, IsoDateTime>>;
  /** Card id to the time it came due while the deck was open. */
  bumped: Readonly<Record<string, IsoDateTime>>;
}

export const isNowDue = (id: string, s: Pick<DeckState, "bumped">, nowIso: IsoDateTime): boolean => {
  const at = s.bumped[id];
  return !!at && Date.parse(nowIso) - Date.parse(at) < NOW_DUE_MIN * 60_000;
};

/**
 * D2, D3, D5: the order the person sees. Locked cards first. Cards in a Later pause go to the back (in their own order).
 * A card that came due while the deck was open goes to the top: first of all, or, when it is not safety-critical, right
 * after the safety-critical cards already at the top.
 */
export function arrangeDeck(cards: readonly DeckCard[], nowIso: IsoDateTime, s: DeckState): DeckCard[] {
  // A pause ends early when the card comes due (D5): the person is told, whatever they put off earlier.
  const paused = (c: DeckCard) => !c.locked && !!s.later[c.id] && s.later[c.id]! > nowIso && !isNowDue(c.id, s, nowIso);
  const locked = cards.filter((c) => c.locked);
  const rest = cards.filter((c) => !c.locked);
  const live = rest.filter((c) => !paused(c));
  const back = rest.filter(paused);
  const bumpedIds = live.filter((c) => isNowDue(c.id, s, nowIso)).sort((a, b) => s.bumped[b.id]!.localeCompare(s.bumped[a.id]!)); // newest first
  const base = live.filter((c) => !bumpedIds.includes(c));
  const bumpedCritical = bumpedIds.filter((c) => c.critical), bumpedOther = bumpedIds.filter((c) => !c.critical);
  // The run of safety-critical cards that already lead `base`: a lesser card never goes above them.
  let lead = 0;
  while (lead < base.length && base[lead]!.critical) lead++;
  const placed = [...bumpedCritical, ...base.slice(0, lead), ...bumpedOther, ...base.slice(lead)];
  return [...locked, ...placed, ...back];
}

/** D5: the cards whose due time passed between two looks at the clock. The first look (no earlier clock) bumps nothing. */
export function newlyDue(cards: readonly DeckCard[], prevIso: IsoDateTime | undefined, nowIso: IsoDateTime): string[] {
  if (!prevIso) return [];
  return cards.filter((c) => !c.locked && !!c.dueAt && c.dueAt > prevIso && c.dueAt <= nowIso).map((c) => c.id);
}

/* ─────────────── gestures (D3, D7) ─────────────── */

export type SwipeIntent = "do" | "later" | "blocked" | "none";

/** How far a card must travel, in px, to count as a swipe: a third of its width, never less than 80. */
export const swipeThreshold = (width: number) => Math.max(80, Math.round(width / 3));

/**
 * What a released swipe means. `dx` > 0 is right (do it), < 0 is left (Later). A locked card (D2) answers "blocked" to a
 * left swipe, and so does a card whose Later is not offered (D4): it springs back; it is never put off.
 */
export function swipeIntent(dx: number, width: number, laterOk: boolean): SwipeIntent {
  const t = swipeThreshold(width);
  if (dx >= t) return "do";
  if (dx <= -t) return laterOk ? "later" : "blocked";
  return "none";
}

/** D7: the same two moves from the keyboard. */
export function keyIntent(key: string): "do" | "later" | undefined {
  return key === "ArrowRight" ? "do" : key === "ArrowLeft" ? "later" : undefined;
}

/** The words, shared by both apps. */
export const DECK_WORDS = {
  later: "Later",
  doIt: "Do it",
  nowDue: "Now due",
  swipeHint: "Swipe right to do it. Swipe left for later.",
  swipeHintFixed: "Swipe right to do it. This one can't be put off.",
  hint: (laterOk: boolean) => (laterOk ? "Swipe right to do it. Swipe left for later." : "Swipe right to do it. This one can't be put off."),
  laterNote: (until: string) => `Later. It comes back at ${until}.`,
  count: (n: number, of: number) => `Card ${n} of ${of}`,
  toggleList: "List",
  toggleCards: "Cards",
  onlyOne: "This is the only one left.",
} as const;
